'use client'

import { useState, useCallback, useEffect } from 'react'
import { BigglesPanel } from './biggles-panel'
import type { ChatMessage, ToolActivity } from './biggles-message'
import { useUserProfile } from '@/hooks/use-user-profile'
import { clientLogger } from '@/lib/client-logger'

const log = clientLogger('BigglesProvider')

export function BigglesProvider() {
  const [isOpen, setIsOpen] = useState(false)
  const { profile } = useUserProfile()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // The mobile header hosts its own compact launcher (the floating button is
  // hidden on phone widths) and opens the panel via this event.
  useEffect(() => {
    const open = () => setIsOpen(true)
    window.addEventListener('biggles:open', open)
    return () => window.removeEventListener('biggles:open', open)
  }, [])

  // Defined before sendMessage so it can appear in sendMessage's deps without TDZ.
  // Empty deps because the body only uses the stable setMessages setter.
  const handleSSEEvent = useCallback(
    (eventType: string, data: Record<string, unknown>, assistantId: string) => {
      switch (eventType) {
        case 'text':
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, content: m.content + (data.text as string) }
                : m
            )
          )
          break

        case 'tool_call':
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== assistantId) return m
              const activity: ToolActivity = {
                tool: data.tool as string,
                status: 'calling',
              }
              // Text streamed before a tool call and text after it are separate
              // thoughts ("Let me check…" / "Looks like…"); without a break
              // they run together as one sentence.
              const content = m.content && !m.content.endsWith('\n') ? `${m.content}\n\n` : m.content
              return {
                ...m,
                content,
                toolActivities: [...(m.toolActivities || []), activity],
              }
            })
          )
          break

        case 'tool_result':
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== assistantId) return m
              const activities = [...(m.toolActivities || [])]
              // Find the last calling activity for this tool and mark done
              for (let i = activities.length - 1; i >= 0; i--) {
                if (
                  activities[i].tool === data.tool &&
                  activities[i].status === 'calling'
                ) {
                  activities[i] = {
                    ...activities[i],
                    status: 'done',
                    result: data.result,
                  }
                  break
                }
              }
              return { ...m, toolActivities: activities }
            })
          )
          break

        case 'error':
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? {
                    ...m,
                    content:
                      m.content ||
                      'Sorry, something went wrong. Please try again.',
                    isStreaming: false,
                  }
                : m
            )
          )
          break

        case 'done':
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, isStreaming: false } : m
            )
          )
          break
      }
    },
    []
  )

  // A canned Biggles line appended WITHOUT a model round-trip. The "Quiz me"
  // chip uses it: the student should see the question instantly, and the
  // first thing that costs a Claude call is their answer to it. The line is
  // part of the transcript, so the model sees it as context on the next turn.
  const addLocalReply = useCallback((text: string) => {
    setMessages((prev) => [
      ...prev,
      { id: `assistant-local-${Date.now()}`, role: 'assistant', content: text, toolActivities: [] },
    ])
  }, [])

  const sendMessage = useCallback(async (text: string) => {
    // Add user message
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
    }

    // Create placeholder assistant message for streaming
    const assistantId = `assistant-${Date.now()}`
    const assistantMessage: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      toolActivities: [],
      isStreaming: true,
    }

    setMessages((prev) => [...prev, userMessage, assistantMessage])
    setIsLoading(true)

    try {
      // Build conversation history for API
      const conversationMessages = [...messages, userMessage].map((m) => ({
        role: m.role as 'user' | 'assistant',
        content: m.content,
      }))

      const response = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: conversationMessages }),
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No response stream')

      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        // Parse SSE events from buffer
        const lines = buffer.split('\n')
        buffer = lines.pop() || '' // Keep incomplete line in buffer

        let eventType = ''
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim()
          } else if (line.startsWith('data: ')) {
            const dataStr = line.slice(6)
            try {
              const data = JSON.parse(dataStr)
              handleSSEEvent(eventType, data, assistantId)
            } catch {
              // Ignore malformed data
            }
          }
        }
      }

      // Mark streaming complete
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId ? { ...m, isStreaming: false } : m
        )
      )
    } catch (error) {
      log.error('Biggles chat error:', error)
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? {
                ...m,
                content: 'Sorry, I hit some turbulence. Please try again.',
                isStreaming: false,
              }
            : m
        )
      )
    } finally {
      setIsLoading(false)
    }
  }, [messages, handleSSEEvent])

  return (
    <>
      {/* The floating launcher is GONE on desktop (2026-08-22): the sidebar's
          Biggles item and the narrow-header button dispatch `biggles:open`
          instead, and the mobile app keeps its own header launcher. Only the
          panel renders from here now. */}

      {/* Chat Panel */}
      <BigglesPanel
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        messages={messages}
        onSend={sendMessage}
        onLocalReply={addLocalReply}
        isLoading={isLoading}
        userName={profile?.firstName || null}
      />
    </>
  )
}
